import store from '@/store'

//
// * Parâmetros de requisição
//
const API_PREFIX = '/Api/1/'
const PROTOCOL = window.location.protocol
const HOST = window.location.hostname
const PORT = window.location.port

/**
 * Requisita uma cópia atualizada do objeto referenciado e o estoca em cache.
 *
 * @param  {String} site Site destino.
 * @param  {String} eref Nome de referência do objeto.
 * @param  {Object} payload Os parâmetros de requisição do objeto.
 * @param  {Object} identity
 *    Identidade alterativa para autenticação da requisição.
 *    Se omitida, a identidade atual do usuário logado no sistema é usada.
 * @returns {Keep.Paper.Design.Status}
 *    Uma instância do objeto Status contendo a situação da requisição e a
 *    entidade baixada do servidor caso bem sucedido.
 *
 *    O objeto Status tem a forma geral:
 * {
 *   # Status geral da requisição.
 *   # Verdadeiro caso a requisição seja bem sucedida.
 *   # Para o código de status '200 - OK' ou qualquer um da série 200-299.
 *   ok: <bool>,
 *
 *   # Código de status da requisição.
 *   # -  000-199   (Reservado.)
 *   # -  200-299   Código HTTP de sucesso.
 *   # -  300-399   (Reservado.)
 *   # -  400-499   Código HTTP de erro por parte do usuário.
 *   # -  500-599   Código HTTP de erro por parte do servidor.
 *   # -  600-699   Código de erro personalizado pelo servidor do Paper.
 *   # -  700-799   Código de erro personalizado pelo cliente do Paper.
 *   #          700 Bad Response
 *   #              A resposta do servidor não era a esperada.
 *   #          710 Bad Response
 *   #              Falha de comunicação com o servidor
 *   code: <integer>,
 *
 *   # Um nome de evento de resposta.
 *   # Em geral usado em definições de navegação entre páginas.
 *   # Por padrão o sistema produz os eventos:
 *   # -  Success, para requisição bem sucedida. (Ok=true)
 *   # -  Fault, para requisição mal sucedida. (Ok=false)
 *   # Porém, qualquer outro texto pode ser utilizado.
 *   name: <string>,
 *
 *   # A mensagem de status.
 *   # Em geral omitida em caso de sucesso (Ok=true).
 *   message: <string>,
 *
 *   # Uma coleção de mensagens adicionais explicativas.
 *   # Em geral omitida em caso de sucesso (Ok=true).
 *   details: <string>[],
 *
 *   # O objeto recebido da requisição.
 *   entity: <Keep.Paper.Design.IEntity>
 * }
 */
export async function fetchEntity(site, eref, payload, identity) {
  if (!eref) {
    throw new Error('O endereço destino não foi definido para esta ação.')
  }
  let object = await _request(site, eref, payload, identity)
  let status = _sanitizeStatus(site, eref, object)
  return status
}

/**
 * Constrói a URI de requisição do objeto referenciado.
 *
 * @param  {String} site Site destino.
 * @param  {String} eref Nome de referência do objeto.
 */
function _createUri(site, eref) {

  // // TODO: Por ora os parametros de URI estão sendo repassados para todas as
  // // TODO: requisições. O ideal seria removê-las da URI e repassá-las apenas
  // // TODO: para as requisições de direito.
  // let query = window.location.search
  const query = ''

  let [ name, args ] = eref.split('(')
  name = name?.replaceAll('.', '/')
  args = (args?.length > 0) ? `(${args}` : ''
  eref = name + args

  const infix = site ? `${site}/` : ''
  const uri = `${PROTOCOL}//${HOST}:${PORT}${API_PREFIX}${infix}${eref}${query}`
  return uri
}

/**
 * Realiza uma requisição para o servidor do Paper.
 *
 * @param  {String} site Site destino.
 * @param  {String} eref Nome de referência do objeto.
 * @param  {Object} payload Os parâmetros de requisição do objeto.
 * @param  {Object} identity
 *    Identidade alterativa para autenticação da requisição.
 *    Se omitida, a identidade atual do usuário logado no sistema é usada.
 * @returns {Keep.Paper.Design.Status}
 *    Uma instância do objeto Status contendo a situação da requisição e a
 *    entidade baixada do servidor caso bem sucedido.
 */
async function _request(site, eref, payload, identity) {
  try {
    let uri = _createUri(site, eref);
    let options = {
      cache: 'no-store',
      credentials: 'omit',
      redirect: 'follow',
      method: 'post',
      headers: new Headers(),
      body: JSON.stringify(payload || {}),
    }

    // Identificando o usuário da requisição...
    if (!identity) {
      identity = store.state.system.identity
    }
    if (identity?.token) {
      options.headers.append('Authorization', `Token ${identity.token}`)
    }
    else if (identity?.username) {
      let claim = `${identity.username}:${identity.password}`
      let basic = Buffer.from(claim)?.toString('Base64')
      options.headers.append('Authorization', `Basic ${basic}`)
    }

    //
    // * Realizando a requisição...
    //
    let response = await fetch(uri, options)
    let object = await response.json()

    return object

  } catch (error) {
    console.error(error)
    return {
      ok: false,
      code: 710,  // Falha de comunicação com o servidor
      outcome: "Fault",
      message: "Houve uma falha de comunicação com o servidor de dados.",
      details: [error.message]
    }
  }
}

/**
 * Checa a estrutura de um objeto {Keep.Paper.Design.Status} e aplica algumas
 * correções padrão suportadas.
 *
 * @param  {String} site Site destino.
 * @param  {Keep.Paper.Design.Eref} eref
 *    A referência do objeto requisitado.
 * @param  {Keep.Paper.Design.Status} data
 *    O objeto de status a ser sanitizado.
 */
function _sanitizeStatus(site, eref, data) {
  let status

  try {
    status = {
      ...data
    }
    status.code = data.code || 700   // A resposta do servidor não era a esperada.
    status.ok = (status.code >= 200 && status.code < 300)

  } catch (error) {
    status = {
      ok: false,
      code: 700,  // A resposta do servidor não era a esperada.
      outcome: "Fault",
      message: "A resposta do servidor não era a esperada.",
      details: [error.message]
    }
  }

  if (!status.entity) {
    status.entity = {
      type: 'Status',
      ...status
    }
    delete status.entity.entity
  }

  status.entity.sref = status.entity.eref
  status.entity.eref = eref

  return status
}
